import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  effect,
  ElementRef,
  inject,
  signal,
  viewChild,
  ViewContainerRef,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, RouterOutlet } from '@angular/router';
import { Store } from '@ngxs/store';
import { map } from 'rxjs';

import {
  collectRouteData,
  collectRouteParams,
  RouteSnapshotService,
} from '@cosmos/router';
import { CosDrawerComponent, CosDrawerModule } from '@cosmos/ui-drawer';
import { injectDestroyRef } from '@cosmos/util-common/di';
import { isIFrame } from '@cosmos/util-common/is-iframe';
import { CosTeleportOutletDirective } from '@cosmos/util-teleport';
import { CosmosUtilTranslationsModule } from '@cosmos/util-translations';
import { CustomerPortalNewFooterComponent } from '@customer-portal/common/feature-new-footer';
import { CustomerPortalNewHeaderComponent } from '@customer-portal/common/feature-new-header';
import { listenToResizeAndNotifyHostWindow } from '@customer-portal/common/ui-layout';
import {
  CustomerPortalScrollContainer,
  DrawerService,
} from '@customer-portal/common/util-layout';
import { PreviewService } from '@customer-portal/data-access-preview';
import {
  CustomerPortalPresentationQueries,
  provideRetainPresentationAccessCodeLinkConfig,
  retainPresentationAccessCodeIfNeeded,
} from '@customer-portal/presentations/data-access-presentations';
import { CustomerPortalProjectQueries } from '@customer-portal/projects/data-access/store';

@Component({
  selector: 'customer-portal-projects-host',
  templateUrl: './projects-host.component.html',
  styleUrls: ['./projects-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    CosmosUtilTranslationsModule,
    CosTeleportOutletDirective,
    CustomerPortalNewHeaderComponent,
    CustomerPortalNewFooterComponent,
    CosDrawerModule,
  ],
  providers: [provideRetainPresentationAccessCodeLinkConfig()],
  host: {
    '[class.within-preview-frame]': 'showPreviewFrame()',
    '[class.drawer-open]': 'drawer().openedSignal()',
  },
})
export class CustomerPortalProjectsHostComponent {
  private readonly _store = inject(Store);

  readonly previewService = inject(PreviewService);

  readonly isIframe = isIFrame();

  readonly routesParam = collectRouteParams(inject(Router)) as Record<
    'projectId' | 'orderId' | 'invoiceId' | 'quoteId',
    string
  >;

  readonly routesData = collectRouteData(inject(Router)) as Record<
    'visitedPage',
    string
  >;

  protected drawerService = inject(DrawerService);
  protected content = viewChild.required('content', {
    read: ElementRef<HTMLElement>,
  });
  protected drawer = viewChild.required(CosDrawerComponent);
  protected drawerContentVcr = viewChild.required('drawerContent', {
    read: ViewContainerRef,
  });

  protected readonly headerHeight = signal(0);

  private readonly _document = inject(DOCUMENT);

  readonly showPreviewFrame = computed(() => {
    return (
      this.previewService.isPreviewMode() &&
      !this.previewService.screenshotMode &&
      !this.isIframe
    );
  });

  // we can't hide the header/footer using media queries since there are pages where we have to hide them on the screen
  vm = toSignal(
    inject(RouteSnapshotService).snapshot$.pipe(
      map((snapshot) => ({
        showHeader: !snapshot?.data?.['hideHeader'],
        showFooter: !snapshot?.data?.['hideFooter'],
        landingMode: !!snapshot?.data?.['landingMode'],
      }))
    ),
    {
      initialValue: {
        showHeader: true,
        showFooter: true,
        landingMode: true,
      },
    }
  );

  constructor() {
    // This host is `height: 100vh; overflow: auto` (see the stylesheet), so it —
    // not the document — is what scrolls. Hand it to the viewport scroller the
    // router uses, otherwise scroll-to-top and back/forward restoration drive
    // `window` and do nothing.
    const hostElement =
      inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
    const scrollContainer = inject(CustomerPortalScrollContainer);
    scrollContainer.register(hostElement);
    injectDestroyRef().onDestroy(() => scrollContainer.unregister(hostElement));

    // run the retain access code logic on projects host level so when user goes to project orders/quotes pages
    // and then back we can restore the access code for presentation routes if needed
    retainPresentationAccessCodeIfNeeded();

    this._listenToFavicon();

    afterNextRender(() => {
      const drawer = this.drawer();
      if (!drawer) {
        return;
      }
      // link the drawer to the service
      this.drawerService.connect(drawer, this.drawerContentVcr());
    });

    injectDestroyRef().onDestroy(() => {
      this.drawerService.disconnect();
    });

    // Since this component is responsible for scrolling
    // it is also responsible for notifying the host about content height changes
    listenToResizeAndNotifyHostWindow({
      // eslint-disable-next-line @nx/workspace-signals
      content: this.content,
    });
  }

  private _listenToFavicon() {
    if (!global_isBrowser) {
      return;
    }
    effect(() => {
      const templateIcon = this._store.selectSignal(
        CustomerPortalPresentationQueries.getTemplatePageIcon
      );
      const projectCustomer = this._store.selectSignal(
        CustomerPortalProjectQueries.getProjectCustomer
      );
      const iconUrl =
        templateIcon()?.FileUrl || projectCustomer()?.IconImageUrl;
      if (iconUrl) {
        const link =
          this._document.querySelector<HTMLLinkElement>('#portal-favicon')!;
        link.href = iconUrl;
      }
    });
  }

  protected menuResize($event: ResizeObserverEntry) {
    this.headerHeight.set(Math.ceil($event.contentRect.height));
  }
}
